import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "./Reveal";
import { ArrowRight } from "lucide-react";
import { base44 } from "@/api/base44Client";

/**
 * Surfaces published Bed Bug Guide articles on high-traffic pages so
 * internal PageRank flows into the guide posts (which sit deep otherwise).
 * `match` filters by a keyword (area, neighbourhood, property type); when
 * too few match, the section tops up with the most recent posts so it always
 * links out. `excludeSlug` avoids self-linking.
 */
export default function RelatedGuides({ match, excludeSlug, limit = 4 }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const today = new Date().toISOString().slice(0, 10);
    base44.entities.BlogPost.list("-published_date", 120)
      .then((rows) => {
        if (!active) return;
        const visible = (rows || [])
          .filter((p) => p.status === "published")
          .filter((p) => !p.published_date || p.published_date <= today)
          .filter((p) => p.slug !== excludeSlug);
        const needle = (match || "").toLowerCase().trim();
        const matched = needle
          ? visible.filter((p) =>
              `${p.title} ${p.excerpt}`.toLowerCase().includes(needle))
          : [];
        const rest = visible.filter((p) => !matched.includes(p));
        setPosts([...matched, ...rest].slice(0, limit));
        setLoading(false);
      })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [match, excludeSlug, limit]);

  if (!loading && posts.length === 0) return null;

  return (
    <Reveal>
      <div>
        <h3 className="font-heading text-base font-bold text-primary">From the Bed Bug Guide</h3>
        <p className="mt-1 text-sm text-muted-foreground">Practical, specialist answers on signs, preparation, treatment and prevention.</p>
        {loading ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {Array.from({ length: limit }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-md border border-border bg-muted/20" />
            ))}
          </div>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {posts.map((p) => (
              <Link key={p.id} to={`/guide/${p.slug}`} className="group flex items-start gap-2 rounded-md border border-border bg-muted/20 p-4 transition-colors hover:border-accent/40 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="mt-0.5 font-heading text-sm font-bold leading-snug text-primary">{p.title}</span>
                <ArrowRight className="ml-auto mt-1 h-4 w-4 shrink-0 text-accent transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        )}
        <Link to="/guide" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">Browse all articles <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </Reveal>
  );
}